import type { Timestamp } from "firebase/firestore";

/**
 * Seconds carried by a Firestore Timestamp that crossed a callable boundary and
 * arrived as plain JSON, or `null` if this value is not one.
 *
 * A callable response serializes a Timestamp to `{_seconds, _nanoseconds}` (some
 * paths produce the unprefixed `{seconds, nanoseconds}`), losing the `toDate`
 * method. Without this branch such a value looks like an ordinary object and
 * falls through to the "now" fallback below — which is not a visibly broken
 * date, it is a plausible wrong one, and every comparison against it silently
 * reports the record as current.
 */
function serializedTimestampSeconds(t: object): number | null {
  const candidate = t as { seconds?: unknown; _seconds?: unknown };
  const seconds = typeof candidate._seconds === "number" ? candidate._seconds : candidate.seconds;
  return typeof seconds === "number" ? seconds : null;
}

function serializedTimestampMs(t: object, seconds: number): number {
  const candidate = t as { nanoseconds?: unknown; _nanoseconds?: unknown };
  const nanos =
    typeof candidate._nanoseconds === "number"
      ? candidate._nanoseconds
      : typeof candidate.nanoseconds === "number"
        ? candidate.nanoseconds
        : 0;
  return seconds * 1000 + nanos / 1e6;
}

/**
 * Normalize a Firestore timestamp value to a `Date`.
 *
 * The same field reaches the client in four shapes: a `Timestamp` from a direct
 * SDK read, a `{_seconds, _nanoseconds}` object from a callable response, an ISO
 * string from a rehydrated store, and a plain `Date` in tests. Unparseable or
 * absent input falls back to "now" so render paths never crash on a malformed
 * timestamp — callers that gate an irreversible action on the result must check
 * for a usable value themselves rather than trusting that fallback.
 */
export function toDate(t: unknown): Date {
  if (!t) return new Date();
  if (t instanceof Date) return t;
  if (typeof t === "object") {
    if (typeof (t as Timestamp).toDate === "function") return (t as Timestamp).toDate();
    const seconds = serializedTimestampSeconds(t);
    if (seconds !== null) return new Date(serializedTimestampMs(t, seconds));
  }
  if (typeof t === "string") return new Date(t);
  return new Date();
}

/**
 * Normalize a Firestore timestamp value to epoch milliseconds.
 *
 * Same representation spread as `toDate`, plus raw epoch numbers. Callers
 * that compare or sort across these representations need a single
 * normalized form. Absent or malformed input is 0.
 */
export function toEpochMs(t: unknown): number {
  const ms = rawEpochMs(t);
  // A malformed value (Invalid Date, NaN) is "no date", never NaN.
  return Number.isFinite(ms) ? ms : 0;
}

function rawEpochMs(t: unknown): number {
  if (!t) return 0;
  if (typeof t === "number") return t;
  if (typeof t === "string") return Date.parse(t);
  if (t instanceof Date) return t.getTime();
  if (typeof (t as Timestamp).toMillis === "function") return (t as Timestamp).toMillis();
  if (typeof t === "object") {
    const seconds = serializedTimestampSeconds(t);
    if (seconds !== null) return serializedTimestampMs(t, seconds);
  }
  return 0;
}
